import { useState, useEffect } from 'react';
import {
  getExchangeRates, setExchangeRate, getAITools, createAITool, updateAITool, getPackages,
  getUsers, createUser, updateUser, deleteUser, formatIQD, formatUSD,
  getExpenseCategories, createExpenseCategory, updateExpenseCategory, deleteExpenseCategory,
  getEmployees
} from '../utils/api';
import { parseArabicToEnglish } from '../utils/formatters';
import api from '../utils/api';
import toast from 'react-hot-toast';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = { navy: 'var(--text-dark-h, #0F1E33)', teal: '#17A589', blue: '#9B8EC4', border: 'var(--border-light, #DDE2E8)', gray: 'var(--bg-flat-50, #F0F4F8)' };

const inp = {
  width: '100%', padding: '8px 12px', border: `1px solid ${C.border}`,
  borderRadius: 8, fontFamily: 'Tajawal, sans-serif', fontSize: 14, boxSizing: 'border-box',
  background: 'var(--input-bg, white)', color: 'var(--text-dark-s, #374151)'
};

const btn = (bg = C.blue, color = '#fff') => ({
  padding: '8px 18px', background: bg, color, border: 'none', borderRadius: 8,
  cursor: 'pointer', fontFamily: 'Tajawal, sans-serif', fontSize: 13, fontWeight: 600
});

function Field({ label, children, required }) {
  return (
    <div>
      <label style={{ fontSize: 13, color: '#7F8C8D', display: 'block', marginBottom: 4 }}>
        {label}{required && <span style={{ color: '#E74C3C' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function Input({ label, required, ...props }) {
  return (
    <Field label={label} required={required}>
      <input {...props} style={{ ...inp, ...(props.style || {}) }} />
    </Field>
  );
}

// ─── Overlay Modal ────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16
    }}>
      <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
        background: 'var(--bg-flat-white, #fff)', borderRadius: 14, width: 480, maxHeight: '90vh',
        overflowY: 'auto', padding: 28, fontFamily: 'Tajawal, sans-serif', direction: 'rtl'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, color: C.navy, fontSize: 16 }}>{title}</h3>
          <button onClick={onClose} className="dark:!text-gray-400" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted, #94A3B8)', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Roles config ─────────────────────────────────────────────────────────────
const ROLES = {
  owner: { label: 'مالك', color: '#DC2626', bg: '#FEE2E2' },
  admin: { label: 'مدير', color: '#7C3AED', bg: '#EDE9FE' },
  manager: { label: 'مشرف', color: '#9B8EC4', bg: '#F0ECF7' },
  supervisor: { label: 'متابع (TM)', color: '#2563EB', bg: '#DBEAFE' },
  team_leader: { label: 'قائد فريق (TM)', color: '#D97706', bg: '#FEF3C7' },
  team_member: { label: 'عضو فريق (TM)', color: '#059669', bg: '#D1FAE5' },
  staff: { label: 'موظف', color: '#17A589', bg: '#D1FAE5' },
  viewer: { label: 'مشاهد فقط', color: 'var(--text-gray, #64748B)', bg: '#F1F5F9' },
};

const DEF_PERMS = {
  owner: { clients: true, invoices: true, expenses: true, subscriptions: true, reports: true, settings: true, delete: true, tm_tasks: true, tm_teams: true, tm_earnings: true },
  admin: { clients: true, invoices: true, expenses: true, subscriptions: true, reports: true, settings: true, delete: true, tm_tasks: true, tm_teams: true, tm_earnings: true },
  manager: { clients: true, invoices: true, expenses: true, subscriptions: true, reports: true, settings: false, delete: false, tm_tasks: false, tm_teams: false, tm_earnings: false },
  supervisor: { clients: true, invoices: false, expenses: false, subscriptions: true, reports: true, settings: false, delete: false, tm_tasks: true, tm_teams: false, tm_earnings: true },
  team_leader: { clients: false, invoices: false, expenses: false, subscriptions: false, reports: false, settings: false, delete: false, tm_tasks: true, tm_teams: true, tm_earnings: true },
  team_member: { clients: false, invoices: false, expenses: false, subscriptions: false, reports: false, settings: false, delete: false, tm_tasks: true, tm_teams: false, tm_earnings: true },
  staff: { clients: true, invoices: true, expenses: false, subscriptions: true, reports: false, settings: false, delete: false, tm_tasks: false, tm_teams: false, tm_earnings: false },
  viewer: { clients: false, invoices: false, expenses: false, subscriptions: false, reports: true, settings: false, delete: false, tm_tasks: false, tm_teams: false, tm_earnings: false },
};

const PERM_LABELS = [
  ['clients', 'إدارة العملاء'], ['invoices', 'الفواتير والمدفوعات'],
  ['expenses', 'المصاريف'], ['subscriptions', 'الاشتراكات'],
  ['reports', 'التقارير'], ['settings', 'الإعدادات'], ['delete', 'الحذف'],
  ['tm_tasks', '📋 المهام V2'], ['tm_teams', '👥 الفرق V2'], ['tm_earnings', '💰 المستحقات V2'],
];

const BLANK = { username: '', password: '', confirmPassword: '', full_name: '', role: 'staff', employee_id: 'auto', permissions: { ...DEF_PERMS.staff } };

// ─── Users Tab ────────────────────────────────────────────────────────────────
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [showInactive, setShowInactive] = useState(false);

  // Current user id (for "أنت" badge)
  const myId = (() => {
    try {
      const b64 = localStorage.getItem('token').split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(b64 + '='.repeat((4 - b64.length % 4) % 4))).id ?? null;
    } catch { return null; }
  })();

  const load = () => {
    setLoading(true);
    Promise.all([
      getUsers().catch(() => []),
      getEmployees().catch(() => [])
    ]).then(([u, e]) => {
      setUsers(u);
      setEmployees(e);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const reset = () => setForm({ ...BLANK, permissions: { ...DEF_PERMS.staff } });

  const openCreate = () => { reset(); setEditUser(null); setShowModal(true); };
  const openEdit = (u) => {
    setEditUser(u);
    const perms = (() => { try { return typeof u.permissions === 'string' ? JSON.parse(u.permissions || '{}') : (u.permissions || {}); } catch { return {}; } })();
    setForm({ username: u.username, full_name: u.full_name || '', role: u.role || 'staff', password: '', confirmPassword: '', employee_id: u.employee_id || '', permissions: perms });
    setShowModal(true);
  };

  const setRole = (role) => setForm(f => ({ ...f, role, permissions: { ...DEF_PERMS[role] } }));
  const togglePerm = (k) => setForm(f => ({ ...f, permissions: { ...f.permissions, [k]: !f.permissions[k] } }));

  const handleSubmit = async () => {
    if (!form.username.trim()) return toast.error('اسم المستخدم مطلوب');
    if (!editUser && !form.password) return toast.error('كلمة السر مطلوبة');
    if (form.password && form.password.length < 6) return toast.error('كلمة السر 6 أحرف على الأقل');
    if (form.password && form.password !== form.confirmPassword) return toast.error('كلمتا السر غير متطابقتين');

    try {
      const payload = { 
        username: form.username.trim(), 
        full_name: form.full_name, 
        role: form.role, 
        permissions: form.permissions, 
        employee_id: (form.employee_id === 'auto' || form.employee_id === '') ? null : Number(form.employee_id),
        create_employee: form.employee_id === 'auto'
      };
      if (form.password) payload.password = form.password;
      if (editUser) {
        await updateUser(editUser.id, { ...payload, is_active: editUser.is_active });
        toast.success('تم تحديث المستخدم ✓');
      } else {
        await createUser(payload);
        toast.success('تم إنشاء المستخدم ✓');
      }
      setShowModal(false); reset(); load();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ'); }
  };

  const handleToggle = async (u) => {
    if (u.id === myId) return toast.error('لا يمكنك تعطيل حسابك الخاص');
    try {
      const perms = (() => { try { return typeof u.permissions === 'string' ? JSON.parse(u.permissions || '{}') : (u.permissions || {}); } catch { return {}; } })();
      await updateUser(u.id, { full_name: u.full_name, role: u.role, is_active: u.is_active ? 0 : 1, permissions: perms });
      toast.success(u.is_active ? 'تم تعطيل المستخدم' : 'تم تفعيل المستخدم');
      load();
    } catch { toast.error('فشلت العملية'); }
  };

  // State for confirm modal
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, item: null, message: '', title: '', force: false });

  const confirmDeleteUser = (u) => {
    if (u.id === myId) return toast.error('لا يمكنك حذف حسابك الخاص');
    setConfirmDialog({
      isOpen: true,
      item: u,
      title: 'تأكيد عملية الحذف',
      message: `هل أنت متأكد من رغبتك في حذف المستخدم "${u.username}" نهائياً؟`,
      force: false
    });
  };

  const handleDelete = async () => {
    const u = confirmDialog.item;
    if (!u) return;
    try { 
      const res = await deleteUser(u.id, confirmDialog.force); 
      toast.success(res?.message || 'تم الحذف بنجاح'); 
      load(); 
    }
    catch (err) { toast.error(err.response?.data?.error || 'خطأ في الحذف'); }
    finally { setConfirmDialog({ isOpen: false, item: null, message: '', title: '', force: false }); }
  };

  return (
    <div>
      {/* Confirm Modal */}
      {confirmDialog.isOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 30, 51, 0.65)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000, backdropFilter: 'blur(4px)'
        }}>
          <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
            background: 'var(--bg-flat-white, #fff)', borderRadius: 16, width: 400, maxWidth: '90%', padding: '32px 24px',
            fontFamily: 'Tajawal, sans-serif', direction: 'rtl', textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: '0 0 10px', color: C.navy, fontSize: 18, fontWeight: 700 }}>
              {confirmDialog.title}
            </h3>
            <p className="dark:!text-gray-400" style={{ margin: '0 0 24px', color: 'var(--text-gray, #64748B)', fontSize: 14, lineHeight: 1.6 }}>
              {confirmDialog.message}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
              <input 
                type="checkbox" 
                id="forceDelete" 
                checked={confirmDialog.force} 
                onChange={(e) => setConfirmDialog(p => ({ ...p, force: e.target.checked }))} 
                style={{ accentColor: '#E74C3C', width: 16, height: 16 }}
              />
              <label htmlFor="forceDelete" style={{ color: '#E74C3C', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                حذف إجباري (يمسح كافة المهام والرواتب والسجلات المرتبطة)
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmDialog({ isOpen: false, item: null, message: '', title: '', force: false })}
                style={btn('#F1F5F9', 'var(--text-gray, #64748B)')}
              >
                إلغاء والتراجع
              </button>
              <button
                onClick={handleDelete}
                style={btn('#E74C3C', '#fff')}
              >
                تأكيد الحذف
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: C.navy }}>إدارة المستخدمين</div>
          <div className="dark:!text-gray-400" style={{ fontSize: 12, color: 'var(--text-muted, #94A3B8)', marginTop: 2 }}>أضف مستخدمين وحدد صلاحياتهم</div>
        </div>
        <button onClick={openCreate} style={btn()}>+ إضافة مستخدم</button>
      </div>

      <div style={{ marginBottom: 15, display: 'flex', justifyContent: 'flex-start' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer', userSelect: 'none', color: 'var(--text-gray, #64748B)' }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          <span>عرض الحسابات المعطلة والموظفين السابقين 📦</span>
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <div className="dark:!text-gray-400" style={{ textAlign: 'center', color: 'var(--text-muted, #94A3B8)', padding: 40 }}>جاري التحميل...</div>
      ) : (
        <div style={{ background: 'var(--bg-flat-white, #fff)', borderRadius: 12, overflowX: "auto", overflowY: "hidden", border: `1px solid ${C.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.gray }}>
                {['الاسم الكامل', 'اسم المستخدم', 'الدور', 'الحالة', ''].map((h, i) => (
                  <th key={i} style={{ padding: '11px 16px', textAlign: 'right', fontSize: 12, color: 'var(--text-gray, #64748B)', fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.filter(u => showInactive ? true : u.is_active).length === 0 ? (
                <tr><td colSpan={5} className="dark:!text-gray-400" style={{ padding: 36, textAlign: 'center', color: 'var(--text-muted, #94A3B8)', fontSize: 14 }}>لا يوجد مستخدمون نشطون</td></tr>
              ) : users.filter(u => showInactive ? true : u.is_active).map((u, i) => {
                const rc = ROLES[u.role] || { label: u.role, color: 'var(--text-gray, #64748B)', bg: '#F1F5F9' };
                const isMe = u.id === myId;
                return (
                  <tr key={u.id} style={{ borderBottom: `1px solid #F1F5F9`, background: i % 2 ? 'var(--bg-flat-50, #FAFAFA)' : 'var(--bg-flat-white, #fff)' }}>
                    <td style={{ padding: '11px 16px', fontWeight: 600, fontSize: 14 }}>
                      {u.full_name || '—'}
                      {isMe && <span style={{ marginRight: 8, fontSize: 11, background: '#F0ECF7', color: C.blue, padding: '2px 8px', borderRadius: 20, fontWeight: 700 }}>أنت</span>}
                    </td>
                    <td className="dark:!text-gray-400" style={{ padding: '11px 16px', color: 'var(--text-gray, #64748B)', fontSize: 13, fontFamily: 'monospace' }}>{u.username}</td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{ background: rc.bg, color: rc.color, padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>{rc.label}</span>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{ background: u.is_active ? '#D1FAE5' : '#FEE2E2', color: u.is_active ? '#065F46' : '#991B1B', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                        {u.is_active ? 'نشط' : 'معطّل'}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openEdit(u)} style={{ background: '#F0ECF7', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 13 }}>✏️</button>
                        {!isMe && <button onClick={() => handleToggle(u)} style={{ background: u.is_active ? '#FEF3C7' : '#D1FAE5', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 13 }}>{u.is_active ? '🔴' : '🟢'}</button>}
                        {!isMe && <button onClick={() => confirmDeleteUser(u)} style={{ background: '#FEE2E2', border: 'none', borderRadius: 7, padding: '5px 10px', cursor: 'pointer', fontSize: 13 }}>🗑️</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <Modal title={editUser ? `تعديل — ${editUser.username}` : 'إضافة مستخدم جديد'} onClose={() => { setShowModal(false); reset(); }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Input label="الاسم الكامل" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="مثال: أحمد محمد" />
            {!editUser && (
              <Input label="اسم المستخدم" required value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value.replace(/\s/g, '') }))} placeholder="بدون مسافات" />
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Input label={editUser ? 'كلمة السر الجديدة' : 'كلمة السر'} required={!editUser} type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={editUser ? 'اتركها فارغة' : ''} />
              <Input label="تأكيد كلمة السر" required={!editUser} type="password" value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))} />
            </div>
            <Field label="الدور" required>
              <select value={form.role} onChange={e => setRole(e.target.value)} style={inp}>
                {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="ربط بموظف">
              <select value={form.employee_id} onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))} style={inp}>
                <option value="auto">-- إنشاء موظف جديد تلقائياً --</option>
                <option value="">-- بدون ربط --</option>
                {employees.filter(emp => emp.active || emp.id === Number(form.employee_id)).map(emp => <option key={emp.id} value={emp.id}>{emp.name} {emp.role ? `(‏${emp.role})` : ''}</option>)}
              </select>
              <div style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginTop: 4 }}>
                سيتم إنشاء ملف موظف بنفس الاسم تلقائياً إذا اخترت إنشاء موظف جديد.
              </div>
            </Field>
            <Field label="الصلاحيات">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, padding: '10px 12px', background: 'var(--bg-flat-50, #F8FAFC)', borderRadius: 8, border: `1px solid ${C.border}` }}>
                {PERM_LABELS.map(([k, label]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!form.permissions[k]} onChange={() => togglePerm(k)} />
                    {label}
                  </label>
                ))}
              </div>
            </Field>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={() => { setShowModal(false); reset(); }} style={btn(C.gray, 'var(--text-gray, #64748B)')}>إلغاء</button>
              <button onClick={handleSubmit} style={btn()}>
                {editUser ? '💾 حفظ التعديلات' : '+ إنشاء المستخدم'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── Backup Tab ───────────────────────────────────────────────────────────────
function BackupTab({ settings, setSettings, saveSettings, saving }) {
  const [restoring, setRestoring] = useState(false);
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, file: null });

  function handleDownload() {
    const token = localStorage.getItem('token');
    const base = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const url = base.replace(/\/api$/, '') + '/api/backup/download';
    // Open in new tab with auth header via fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error('فشل التحميل');
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'agency_backup_' + new Date().toISOString().split('T')[0] + '.json';
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(e => toast.error(e.message));
  }

  function onFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setConfirmDialog({ isOpen: true, file });
    e.target.value = '';
  }

  function handleRestoreConfirm() {
    const file = confirmDialog.file;
    if (!file) return;
    setConfirmDialog({ isOpen: false, file: null });
    setRestoring(true);
    const fd = new FormData();
    fd.append('db', file);
    api.post('/backup/restore', fd, { 
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 5 * 60 * 1000 // 5 minutes for large restore uploads
    })
      .then(r => toast.success(r.data.message))
      .catch(e => toast.error(e.response?.data?.error || 'فشل الاستعادة'))
      .finally(() => setRestoring(false));
  }

  function handleSendTelegram() {
    setSendingTelegram(true);
    api.post('/settings/telegram-backup')
      .then(r => toast.success(r.data.message))
      .catch(e => toast.error(e.response?.data?.error || 'فشل إرسال النسخة عبر تيليجرام'))
      .finally(() => setSendingTelegram(false));
  }

  return (
    <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Confirm Restore Modal */}
      {confirmDialog.isOpen && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 30, 51, 0.65)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 10000, backdropFilter: 'blur(4px)'
        }}>
          <div className="dark:!bg-white/5 dark:!backdrop-blur-xl dark:!border-white/10 dark:!text-gray-100 dark:!shadow-none" style={{
            background: 'var(--bg-flat-white, #fff)', borderRadius: 16, width: 400, maxWidth: '90%', padding: '32px 24px',
            fontFamily: 'Tajawal, sans-serif', direction: 'rtl', textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
          }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ margin: '0 0 10px', color: C.navy, fontSize: 18, fontWeight: 700 }}>
              تحذير خطير
            </h3>
            <p className="dark:!text-gray-400" style={{ margin: '0 0 24px', color: 'var(--text-gray, #64748B)', fontSize: 14, lineHeight: 1.6 }}>
              هذا الإجراء سيستبدل قاعدة البيانات الحالية بالكامل ولن تتمكن من التراجع. هل أنت متأكد من المتابعة؟
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmDialog({ isOpen: false, file: null })}
                style={btn('#F1F5F9', 'var(--text-gray, #64748B)')}
              >
                إلغاء والتراجع
              </button>
              <button
                onClick={handleRestoreConfirm}
                style={btn('#E74C3C', '#fff')}
              >
                نعم، استعادة النسخة
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: '#EAFAF1', border: '1px solid #ABEBC6', borderRadius: 12, padding: 20 }}>
        <h3 style={{ margin: '0 0 6px', color: '#1E8449' }}>✅ النسخ الاحتياطي التلقائي مفعّل</h3>
        <p style={{ margin: 0, fontSize: 14, color: '#27AE60' }}>يحفظ نسخة كل 12 ساعة تلقائياً — صيغة SQLite</p>
      </div>

      <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h3 style={{ margin: 0, color: C.navy, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>🤖</span> إعدادات النسخ الاحتياطي عبر تيليجرام
        </h3>
        <p className="dark:!text-gray-400" style={{ margin: 0, fontSize: 13, color: 'var(--text-gray, #64748B)' }}>
          أدخل توكن البوت ومعرف الدردشة لتفعيل النسخ التلقائي كل 12 ساعة والإرسال الفوري.
        </p>
        <Input label="Telegram Bot Token" value={settings?.telegram_bot_token || ''} onChange={e => setSettings({ ...settings, telegram_bot_token: e.target.value })} placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11" />
        <Input label="Telegram Chat ID" value={settings?.telegram_chat_id || ''} onChange={e => setSettings({ ...settings, telegram_chat_id: e.target.value })} placeholder="مثال: 123456789" />

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <button onClick={saveSettings} disabled={saving} style={btn(C.navy)}>
            {saving ? 'جار الحفظ...' : 'حفظ الإعدادات'}
          </button>

          <button onClick={handleSendTelegram} disabled={sendingTelegram || !(settings?.telegram_bot_token)} style={btn(C.teal)}>
            {sendingTelegram ? '⏳ جاري الإرسال...' : '📤 إرسال نسخة الآن'}
          </button>
        </div>
      </div>

      <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h3 style={{ margin: 0, color: C.navy }}>تحميل نسخة احتياطية</h3>
        <p className="dark:!text-gray-400" style={{ margin: 0, fontSize: 13, color: 'var(--text-gray, #64748B)' }}>حمّل قاعدة البيانات الحالية كملف .json لحفظها خارجياً</p>
        <button onClick={handleDownload} style={btn('#17A589')}>
          ⬇️ تحميل قاعدة البيانات الآن
        </button>
      </div>

      <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h3 style={{ margin: 0, color: C.navy }}>استعادة من نسخة احتياطية</h3>
        <p className="dark:!text-gray-400" style={{ margin: 0, fontSize: 13, color: 'var(--text-gray, #64748B)' }}>ارفع ملف .json لاستعادة البيانات — <strong style={{ color: '#E74C3C' }}>سيُستبدل كل شيء</strong></p>
        <label style={{ ...btn('#E74C3C'), display: 'inline-block', textAlign: 'center', cursor: 'pointer' }}>
          {restoring ? '⏳ جاري الاستعادة...' : '⬆️ رفع ملف النسخ الاحتياطي'}
          <input type="file" accept=".json" onChange={onFileChange} style={{ display: 'none' }} disabled={restoring} />
        </label>
      </div>
    </div>
  );
}

// SETTINGS PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function SettingsPage() {
  const [tab, setTab] = useState('users');
  const [settings, setSettings] = useState({});
  const [rates, setRates] = useState([]);
  const [tools, setTools] = useState([]);
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newRate, setNewRate] = useState('');
  const [newTool, setNewTool] = useState({ name: '', monthly_cost_usd: '', monthly_cost_iqd: '', renew_date: '', note: '' });
  const [newCategory, setNewCategory] = useState({ key: '', label: '', color: 'var(--text-gray, #64748B)' });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const res = await api.post('/settings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 2 * 60 * 1000 // 2 minutes
      });
      setSettings(prev => ({ ...prev, company_logo_url: res.data.url }));
      toast.success('تم رفع الشعار بنجاح!');
    } catch (err) {
      console.error(err);
      toast.error('فشل رفع الشعار');
    } finally {
      setUploadingLogo(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [r, t, p, c] = await Promise.all([getExchangeRates(), getAITools(), getPackages(), getExpenseCategories()]);
      setRates(r); setTools(t); setPackages(p); setCategories(c);
    } catch { toast.error('خطأ في تحميل الإعدادات'); }
    try {
      const s = await api.get('/settings/system').then(r => r.data);
      const obj = {};
      (s || []).forEach(item => { obj[item.key] = item.value; });
      setSettings(obj);
    } catch { }
  };

  const saveSettings = async () => {
    setSaving(true);
    try { await api.post('/settings/system', settings); toast.success('تم حفظ الإعدادات'); }
    catch (err) { toast.error(err.response?.data?.error || 'خطأ في الحفظ: ' + err.message); }
    setSaving(false);
  };

  const handleAddRate = async () => {
    if (!newRate) return toast.error('أدخل سعر الصرف');
    try {
      await setExchangeRate({ usd_to_iqd: parseFloat(newRate), rate_date: new Date().toISOString().split('T')[0] });
      toast.success('تم تحديث سعر الصرف'); setNewRate(''); loadAll();
    } catch { toast.error('خطأ'); }
  };

  const handleAddTool = async () => {
    if (!newTool.name) return toast.error('أدخل اسم الأداة');
    try {
      await createAITool(newTool);
      toast.success('تمت الإضافة');
      setNewTool({ name: '', monthly_cost_usd: '', monthly_cost_iqd: '', renew_date: '', note: '' });
      loadAll();
    } catch { toast.error('خطأ'); }
  };

  const toggleTool = async (tool) => {
    try { await updateAITool(tool.id, { is_active: tool.is_active ? 0 : 1 }); toast.success('تم التحديث'); loadAll(); }
    catch { toast.error('خطأ'); }
  };

  const handleAddCategory = async () => {
    if (!newCategory.key || !newCategory.label) return toast.error('رقم التصنيف (Key) والاسم مطلوبان');
    try {
      await createExpenseCategory({ ...newCategory, key: newCategory.key.replace(/\s+/g, '_').toLowerCase() });
      toast.success('تم إضافة التصنيف');
      setNewCategory({ key: '', label: '', color: 'var(--text-gray, #64748B)' });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ في الإصافة'); }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('ملاحظة: هذا قد يؤثر على المصاريف السابقة المرتبطة به. المتابعة؟')) return;
    try {
      await deleteExpenseCategory(id);
      toast.success('تم الحذف');
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'خطأ'); }
  };

  const TABS = [
    { id: 'company', label: '🏢 بيانات الشركة' },
    { id: 'rates', label: '💱 سعر الصرف' },
    { id: 'packages', label: '📦 الباقات' },
    { id: 'tools', label: '🤖 أدوات AI' },
    { id: 'categories', label: '🏷️ تصنيفات المصاريف' },
    { id: 'tm', label: '📋 إدارة المهام TM' },
    { id: 'invoice', label: '🧾 الفواتير' },
    { id: 'backup', label: '💾 النسخ الاحتياطي' },
    { id: 'users', label: '👥 المستخدمون' },
  ];

  const tabBtn = (id) => ({
    padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
    fontFamily: 'Tajawal, sans-serif', fontSize: 13, marginBottom: -2,
    borderBottom: tab === id ? `2px solid ${C.blue}` : '2px solid transparent',
    color: tab === id ? C.blue : '#7F8C8D',
    fontWeight: tab === id ? 700 : 400,
  });

  return (
    <div style={{ padding: 24, fontFamily: 'Tajawal, sans-serif', direction: 'rtl' }}>
      <h1 style={{ margin: '0 0 24px', color: C.navy }}>إعدادات النظام</h1>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: `2px solid ${C.border}`, flexWrap: 'wrap' }}>
        {TABS.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={tabBtn(t.id)}>{t.label}</button>)}
      </div>

      {/* ── Company ── */}
      {tab === 'company' && (
        <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: '#F8FAFC', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13, color: '#7F8C8D', fontWeight: 600 }}>شعار الشركة (اللوكو)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ 
                width: 80, height: 80, borderRadius: 8, background: '#fff', border: `1px dashed ${C.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden'
              }}>
                {settings.company_logo_url ? (
                  <img src={settings.company_logo_url} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                ) : (
                  <span style={{ color: '#CBD5E1', fontSize: 24 }}>🖼️</span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <input type="file" id="logoUpload" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} disabled={uploadingLogo} />
                <label htmlFor="logoUpload" style={{ ...btn(C.navy), display: 'inline-block', opacity: uploadingLogo ? 0.6 : 1, cursor: uploadingLogo ? 'not-allowed' : 'pointer' }}>
                  {uploadingLogo ? 'جار الرفع...' : 'رفع صورة الشعار'}
                </label>
                {settings.company_logo_url && (
                  <button onClick={() => setSettings(prev => ({...prev, company_logo_url: ''}))} style={{ ...btn('transparent', '#E74C3C'), border: '1px solid #E74C3C', marginRight: 8 }}>
                    إزالة
                  </button>
                )}
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 8 }}>
                  يفضل أن تكون بصيغة PNG وبخلفية شفافة وحجم أقل من 5 ميغابايت.
                </div>
              </div>
            </div>
          </div>

          <Input label="اسم الشركة / الوكالة" value={settings.company_name || ''} onChange={e => setSettings({ ...settings, company_name: e.target.value })} placeholder="MS MEDIA" />
          <Input label="رقم الهاتف" value={settings.company_phone || ''} onChange={e => setSettings({ ...settings, company_phone: e.target.value })} placeholder="+964 7xx xxx xxxx" />
          <Input label="العنوان (اختياري)" value={settings.company_address || ''} onChange={e => setSettings({ ...settings, company_address: e.target.value })} placeholder="بغداد، العراق" />
          <div>
            <label style={{ fontSize: 13, color: '#7F8C8D', display: 'block', marginBottom: 4 }}>العملة الأساسية</label>
            <select value={settings.primary_currency || 'IQD'} onChange={e => setSettings({ ...settings, primary_currency: e.target.value })} style={inp}>
              <option value="IQD">دينار عراقي (IQD)</option>
              <option value="USD">دولار أمريكي (USD)</option>
            </select>
          </div>
          <div style={{ background: '#FEF9E7', border: '1px solid #F9CA24', borderRadius: 8, padding: 12, fontSize: 13 }}>
            <strong>⚠️ الضريبة:</strong>
            <div style={{ display: 'flex', gap: 16, marginTop: 8, alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={settings.tax_enabled === '1'} onChange={e => setSettings({ ...settings, tax_enabled: e.target.checked ? '1' : '0' })} />
                تفعيل الضريبة
              </label>
              {settings.tax_enabled === '1' && (
                <input type="text" inputMode="decimal" value={settings.tax_rate || '0'} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setSettings({ ...settings, tax_rate: e.target.value })} placeholder="%" style={{ ...inp, width: 80 }} />
              )}
            </div>
          </div>
          <button onClick={saveSettings} disabled={saving} style={{ ...btn(), alignSelf: 'flex-start', padding: '10px 24px', fontSize: 15 }}>
            {saving ? 'جار الحفظ...' : 'حفظ الإعدادات'}
          </button>
        </div>
      )}

      {/* ── Exchange Rates ── */}
      {tab === 'rates' && (
        <div style={{ maxWidth: 600 }}>
          <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 16px', color: C.navy }}>تحديث سعر الصرف</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <Input label="سعر الدولار بالدينار" type="text" inputMode="decimal" value={newRate} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setNewRate(e.target.value)} placeholder="1500" />
              </div>
              <button onClick={handleAddRate} style={btn()}>تحديث</button>
            </div>
          </div>
          <h3 style={{ color: C.navy }}>سجل أسعار الصرف</h3>
          <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, overflowX: "auto", overflowY: "hidden" }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: C.gray }}>
                  {['التاريخ', '1$ = ؟ دينار', 'ملاحظة'].map(h => <th key={h} style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: '#7F8C8D' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rates.slice(0, 10).map((r, i) => (
                  <tr key={r.id} style={{ borderTop: `1px solid ${C.border}`, background: i === 0 ? '#EAFAF1' : 'var(--bg-flat-white, #fff)' }}>
                    <td style={{ padding: '10px 16px', fontSize: 14 }}>{r.rate_date} {i === 0 && <span style={{ color: '#27AE60', fontSize: 11, fontWeight: 700 }}>● الحالي</span>}</td>
                    <td style={{ padding: '10px 16px', fontSize: 14, fontWeight: 700, color: C.navy }}>{formatIQD(r.usd_to_iqd || 0)}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: '#7F8C8D' }}>{r.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Packages ── */}
      {tab === 'packages' && (
        <div>
          <p style={{ color: '#7F8C8D', fontSize: 14, marginTop: 0 }}>الباقات المتاحة في النظام</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
            {packages.map(p => (
              <div key={p.id} style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
                <h3 style={{ margin: '0 0 8px', color: C.navy }}>{p.name}</h3>
                <p style={{ margin: '0 0 12px', color: '#7F8C8D', fontSize: 13 }}>{p.description}</p>
                <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {[['الثواني المشمولة', `${p.included_seconds}ث`], ['السعر', `${formatUSD(p.price_usd)} / ${formatIQD(p.price_iqd || 0)}`], ['سعر التجاوز', `${formatUSD(p.overage_per_sec_usd)}/ثانية`], ['AI Influencer', p.includes_influencer ? '✅ مشمول' : '❌ غير مشمول'], ['إدارة حسابات', p.includes_management ? '✅ مشمول' : '❌ غير مشمول']].map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}><span>{k}:</span><strong>{v}</strong></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── AI Tools ── */}
      {tab === 'tools' && (
        <div style={{ maxWidth: 700 }}>
          <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 16px', color: C.navy }}>إضافة أداة جديدة</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <Input label="اسم الأداة *" value={newTool.name} onChange={e => setNewTool({ ...newTool, name: e.target.value })} placeholder="HeyGen / ElevenLabs / Midjourney..." />
              </div>
              <Input label="التكلفة الشهرية ($)" type="text" inputMode="decimal" value={newTool.monthly_cost_usd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setNewTool({ ...newTool, monthly_cost_usd: e.target.value })} />
              <Input label="التكلفة الشهرية (د.ع)" type="text" inputMode="decimal" value={newTool.monthly_cost_iqd} onInput={(e) => { e.target.value = parseArabicToEnglish(e.target.value).replace(/[^0-9.]/g, ''); }} onChange={e => setNewTool({ ...newTool, monthly_cost_iqd: e.target.value })} />
              <Input label="تاريخ التجديد" type="date" value={newTool.renew_date} onChange={e => setNewTool({ ...newTool, renew_date: e.target.value })} />
              <Input label="ملاحظة" value={newTool.note} onChange={e => setNewTool({ ...newTool, note: e.target.value })} />
            </div>
            <button onClick={handleAddTool} style={{ ...btn(C.teal), marginTop: 12 }}>إضافة الأداة</button>
          </div>
          <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, overflowX: "auto", overflowY: "hidden" }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: C.gray }}>
                  {['الأداة', 'تكلفة شهرية', 'تجديد', 'الحالة'].map(h => <th key={h} style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: '#7F8C8D' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {tools.map(t => (
                  <tr key={t.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: '10px 16px', fontSize: 14, fontWeight: 600 }}>{t.name}</td>
                    <td style={{ padding: '10px 16px', fontSize: 14 }}>${t.monthly_cost_usd}</td>
                    <td style={{ padding: '10px 16px', fontSize: 13, color: '#7F8C8D' }}>{t.renew_date || '—'}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <button onClick={() => toggleTool(t)} style={{ padding: '4px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', background: t.is_active ? '#EAFAF1' : '#FDEDEC', color: t.is_active ? '#27AE60' : '#E74C3C', fontSize: 12, fontWeight: 700, fontFamily: 'Tajawal, sans-serif' }}>
                        {t.is_active ? '● نشط' : '○ متوقف'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Expense Categories ── */}
      {tab === 'categories' && (
        <div style={{ maxWidth: 600 }}>
          <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 16px', color: C.navy }}>إضافة تصنيف مصروف جديد</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) minmax(150px, 2fr) 80px', gap: 12, alignItems: 'flex-end' }}>
              <Input label="رقم التصنيف بالإنجليزية (Key) *" value={newCategory.key} onChange={e => setNewCategory({ ...newCategory, key: e.target.value })} placeholder="مثال: transport" />
              <Input label="الاسم المعروض (عربي) *" value={newCategory.label} onChange={e => setNewCategory({ ...newCategory, label: e.target.value })} placeholder="مثال: نقل ومواصلات" />
              <Field label="اللون">
                <input type="color" value={newCategory.color} onChange={e => setNewCategory({ ...newCategory, color: e.target.value })} style={{ ...inp, padding: '2px 4px', height: 38, cursor: 'pointer' }} />
              </Field>
            </div>
            <button onClick={handleAddCategory} style={{ ...btn(C.teal), marginTop: 12 }}>+ إضافة التصنيف</button>
          </div>

          <h3 style={{ color: C.navy, marginBottom: 12 }}>التصنيفات الحالية</h3>
          <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, overflowX: "auto", overflowY: "hidden" }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: C.gray }}>
                  {['التصنيف (Key)', 'الاسم المعروض', 'اللون', 'النوع', ''].map(h => <th key={h} style={{ padding: '10px 16px', textAlign: 'right', fontSize: 13, color: '#7F8C8D' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {categories.map(c => (
                  <tr key={c.id} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: '10px 16px', fontSize: 13, fontFamily: 'monospace' }}>{c.key}</td>
                    <td style={{ padding: '10px 16px', fontSize: 14, fontWeight: 600 }}>{c.label}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 14, height: 14, borderRadius: 3, background: c.color }} />
                        <span style={{ fontSize: 12, color: '#7F8C8D' }}>{c.color}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 16px', fontSize: 12 }}>
                      {c.is_system ? <span style={{ color: '#E74C3C', background: '#FDEDEC', padding: '2px 8px', borderRadius: 12 }}>أساسي للنظام</span> : <span style={{ color: '#27AE60', background: '#EAFAF1', padding: '2px 8px', borderRadius: 12 }}>مخصص</span>}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      {!c.is_system && (
                        <button onClick={() => handleDeleteCategory(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#E74C3C' }}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── TM Settings ── */}
      {tab === 'tm' && (
        <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: 'var(--bg-flat-white, #fff)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', color: C.navy }}>إعدادات نظام إدارة المهام (Task Manager V2)</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Input 
                label="تسعيرة الثانية الاحتياطية (Default/Fallback) (د.ع)" 
                type="number" 
                step="0.01"
                value={settings.tm_rate_per_second || '500'} 
                onChange={e => setSettings({ ...settings, tm_rate_per_second: e.target.value })} 
                placeholder="مثال: 500" 
              />
              <div style={{ background: '#FEF9E7', border: '1px solid #F9CA24', borderRadius: 8, padding: 12, fontSize: 13 }}>
                <strong>💡 ملاحظة:</strong> يتم استخدام هذه التسعيرة كقيمة افتراضية (احتياطية) لحساب مستحقات قادة الفرق <b>فقط</b> في حال لم يتم تحديد تسعيرة مخصصة لقائد الفريق في صفحة (إدارة الفرق).
              </div>
            </div>
            <button onClick={saveSettings} disabled={saving} style={{ ...btn(C.navy), marginTop: 16, padding: '10px 24px', fontSize: 15 }}>
              {saving ? 'جار الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </div>
      )}

      {/* ── Invoice Settings ── */}
      {tab === 'invoice' && (
        <div style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label="لغة الفاتورة">
            <select value={settings.invoice_language || 'ar'} onChange={e => setSettings({ ...settings, invoice_language: e.target.value })} style={inp}>
              <option value="ar">عربي</option>
              <option value="en">English</option>
              <option value="both">ثنائي اللغة</option>
            </select>
          </Field>
          <Input label="بادئة رقم الفاتورة" value={settings.invoice_prefix || 'INV'} onChange={e => setSettings({ ...settings, invoice_prefix: e.target.value })} placeholder="INV" />
          <div style={{ background: '#EBF5FB', border: '1px solid #AED6F1', borderRadius: 8, padding: 12, fontSize: 13, color: '#2471A3' }}>
            مثال: {settings.invoice_prefix || 'INV'}-2025-0001
          </div>
          
          <Input label="أيام استحقاق الفاتورة الافتراضي" type="number" value={settings.default_due_days || '7'} onChange={e => setSettings({ ...settings, default_due_days: e.target.value })} placeholder="7" />
          
          <Field label="بيانات التحويل البنكي أو الدفع (مثال: ZainCash)">
            <textarea
              value={settings.bank_transfer_details || ''}
              onChange={e => setSettings({ ...settings, bank_transfer_details: e.target.value })}
              placeholder={"مثال:\nZainCash: 078xxxxxxx\nأو رقم الحساب البنكي..."}
              rows={3}
              style={{ ...inp, resize: 'vertical', minHeight: 70, lineHeight: 1.7 }}
            />
          </Field>
          <Field label="شروط وأحكام الفاتورة">
            <textarea
              value={settings.invoice_terms || ''}
              onChange={e => setSettings({ ...settings, invoice_terms: e.target.value })}
              placeholder={"مثال:\n- الأسعار لا تشمل الضريبة\n- يتم الدفع خلال 7 أيام من تاريخ الإصدار\n- لا يمكن استرداد المبالغ المدفوعة"}
              rows={5}
              style={{ ...inp, resize: 'vertical', minHeight: 90, lineHeight: 1.7 }}
            />
            <div className="dark:!text-gray-400" style={{ fontSize: 11, color: 'var(--text-muted, #94A3B8)', marginTop: 4 }}>
              📌 ستظهر هذه الشروط في أسفل الفاتورة عند الطباعة — كل سطر يُعتبر بند منفصل
            </div>
          </Field>
          <button onClick={saveSettings} disabled={saving} style={{ ...btn(), alignSelf: 'flex-start', padding: '10px 24px', fontSize: 15 }}>
            {saving ? 'جار الحفظ...' : 'حفظ'}
          </button>
        </div>
      )}

      {/* ── Backup ── */}
      {tab === 'backup' && <BackupTab settings={settings} setSettings={setSettings} saveSettings={saveSettings} saving={saving} />}

      {/* ── Users ── */}
      {tab === 'users' && <UsersTab />}
    </div>
  );
}
